import { useState } from 'react'

import { Button, DialogFooter, LoadingButton } from '@fortem-monorepo/react-ui'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'
import type { Strategies } from '@fortem-monorepo/web-sdk'

import { useUpdateStrategyMutation } from '@/react-query/mutations/strategies'
import { useStrategyOptions } from '@/react-query/queries/strategies'

import { UpdateStrategyForm } from '../forms/UpdateStrategyForm/UpdateStrategyForm'
import { toUpdateStrategyValues } from '../forms/UpdateStrategyForm/UpdateStrategyForm.defaults'
import type { UpdateStrategyValues } from '../forms/UpdateStrategyForm/UpdateStrategyForm.schema'
import { UpdateStrategyFormFields } from '../forms/UpdateStrategyForm/UpdateStrategyFormFields'
import { toUpdateStrategyParams } from '../forms/strategy-params'
import { getStrategyStatusLabel } from '../strategy-labels'
import { StrategyDialogFrame } from './StrategyDialogFrame'
import { StrategyOptionsFeedback } from './StrategyOptionsFeedback'
import { StrategyReviewTable } from './StrategyReviewTable'

export type UpdateStrategyDialogProps = { strategy: Strategies.Strategy; onClose: () => void }

export const UpdateStrategyDialog = ({ strategy, onClose }: UpdateStrategyDialogProps) => {
  const optionsQuery = useStrategyOptions()
  const mutation = useUpdateStrategyMutation()
  const [review, setReview] = useState<UpdateStrategyValues | null>(null)

  const canReview = optionsQuery.isSuccess && !mutation.isPending

  const confirm = async () => {
    if (!review || mutation.isPending) {
      return
    }

    const params = toUpdateStrategyParams(review, strategy.strategy_id)

    try {
      await mutation.mutateAsync(params)

      onClose()
    } catch {
      // The hook reports the error; preserve the review and input.
    }
  }

  return (
    <StrategyDialogFrame
      title={review ? 'Confirm strategy update' : `Update ${strategy.strategy_name}`}
      description={
        review ? 'Review the details and confirm to save.' : 'Enter the strategy details, then review before saving.'
      }
      pending={mutation.isPending}
      onClose={onClose}
    >
      <StrategyOptionsFeedback query={optionsQuery} label="strategy types" />
      <UpdateStrategyForm
        strategyTypes={optionsQuery.data?.strategy_types ?? []}
        defaultValues={toUpdateStrategyValues(strategy)}
        onSubmit={(values) => {
          if (review || !canReview) {
            return
          }

          setReview({ ...values })
        }}
      >
        {({ formState }, submit) => (
          <>
            <div hidden={!!review}>
              <UpdateStrategyFormFields
                strategyTypes={optionsQuery.data?.strategy_types ?? []}
                disabled={!canReview || !!review}
              />
            </div>
            {review && (
              <StrategyReviewTable
                headers={['Field', 'Current', 'Updated']}
                rows={[
                  ['Strategy name', strategy.strategy_name, review.strategy_name],
                  ['Strategy type', strategy.strategy_type, review.strategy_type],
                  ['Currency', strategy.currency ?? '—', strategy.currency ?? '—'],
                  [
                    'Management',
                    strategy.is_internal ? 'INTERNAL' : 'EXTERNAL',
                    review.is_internal === 'true' ? 'INTERNAL' : 'EXTERNAL',
                  ],
                  ['Status', getStrategyStatusLabel(strategy.active), review.active === 'true' ? 'ACTIVE' : 'INACTIVE'],
                ]}
              />
            )}

            {mutation.isError && (
              <p role="alert" className="text-sm text-destructive my-3">
                {getErrorMessage(mutation.error)}
              </p>
            )}
            <DialogFooter className="mt-4">
              <Button type="button" size="sm" variant="outline" disabled={mutation.isPending} onClick={onClose}>
                Cancel
              </Button>
              {review ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={mutation.isPending}
                    onClick={() => setReview(null)}
                  >
                    Back
                  </Button>
                  <LoadingButton type="button" size="sm" disabled={mutation.isPending} onClick={confirm}>
                    Confirm update
                  </LoadingButton>
                </>
              ) : (
                <LoadingButton
                  type="submit"
                  size="sm"
                  disabled={!formState.isValid || formState.isSubmitting || !canReview}
                  onClick={async (event) => {
                    event.preventDefault()
                    await submit()
                  }}
                >
                  Review update
                </LoadingButton>
              )}
            </DialogFooter>
          </>
        )}
      </UpdateStrategyForm>
    </StrategyDialogFrame>
  )
}
